import type { AnalyticsEvent } from "./analytics-event";
import type { Provider } from "./provider";
import type { ProviderEvent } from "./provider-event";
import { EventEmitter } from "./event-emitter";
import { useAnalyticsStore } from "./pinia-store";
import type { InternalEvent } from "./internal-event";
import type { AnyEvent } from "./any-event";
import type { SuperProperties } from "./super-properties";

export interface AnalyticsInterface {
  trackEvent: (event: AnalyticsEvent) => void;
  releaseQueue: () => void;
  addProvider: (provider: Provider) => void;
  setSuperProperties: (properties: SuperProperties) => void;
  anonymize: () => void;
  identify: (
    user: Extract<InternalEvent, { name: "userIdentify" }>["payload"],
  ) => void;
}

type CallbackPayload<K> = Extract<AnyEvent, { name: K }>["payload"];

export class Analytics implements AnalyticsInterface {
  private emitter: EventEmitter<AnyEvent>;
  public readonly store: ReturnType<typeof useAnalyticsStore>;

  constructor() {
    this.store = useAnalyticsStore();
    this.emitter = new EventEmitter<AnyEvent>();

    // Bind methods to the correct context
    this.subscribe = this.subscribe.bind(this);
    this.trackEvent = this.trackEvent.bind(this);
    this.fireOrQueueEvent = this.fireOrQueueEvent.bind(this);
    this.releaseQueue = this.releaseQueue.bind(this);
    this.lockQueue = this.lockQueue.bind(this);
    this.setSuperProperties = this.setSuperProperties.bind(this);
    this.addProvider = this.addProvider.bind(this);
    this.identify = this.identify.bind(this);
    this.anonymize = this.anonymize.bind(this);

    this.lockQueue();
  }

  subscribe<K extends AnyEvent["name"]>(
    provider: string,
    eventName: K,
    callback: (payload: CallbackPayload<K>) => ProviderEvent,
  ): void {
    const wrappedCallback = (payload: CallbackPayload<K>): ProviderEvent => {
      const result = callback(payload);
      this.store.markEventAsSent(provider, result);
      return result;
    };

    // @ts-expect-error can't infer the callback param type
    this.emitter.on(eventName, wrappedCallback);
  }

  trackEvent(event: AnalyticsEvent): void {
    this.fireOrQueueEvent(event);
  }

  lockQueue(): void {
    this.store.$patch({ isOnHold: true });
  }

  releaseQueue(): void {
    this.store.$patch({ isOnHold: false });
    this.processQueue();
  }

  addProvider(provider: Provider): void {
    provider.install(this);
  }

  anonymize() {
    const event: InternalEvent = {
      name: "userAnonymize",
      payload: {},
    };
    this.fireOrQueueEvent(event);
    this.store.$patch({ user: undefined });
  }

  identify(user: Extract<InternalEvent, { name: "userIdentify" }>["payload"]) {
    const event: InternalEvent = {
      name: "userIdentify",
      payload: user,
    };
    this.fireOrQueueEvent(event);
    this.store.$patch({ user });
  }

  setSuperProperties(properties: SuperProperties) {
    this.store.$patch({
      superProperties: {
        ...this.store.superProperties,
        ...properties,
      },
    });

    const event: InternalEvent = {
      name: "setSuperProperties",
      payload: this.store.superProperties,
    };
    this.fireOrQueueEvent(event);
  }

  private fireOrQueueEvent(event: AnyEvent): void {
    if (this.store.isOnHold) {
      this.queueEvent(event);
    } else {
      this.fireEvent(event);
    }
  }

  private fireEvent(event: AnyEvent): void {
    this.emitter.emit(event);
    this.store.firedEvents.push(event);
  }

  private queueEvent(event: AnyEvent): void {
    this.store.eventsQueue.push(event);
  }

  private processQueue(): void {
    if (this.store.isOnHold) return;

    while (this.store.eventsQueue.length > 0) {
      const event = this.store.getNextQueuedEvent();
      if (!event) return;
      this.fireEvent(event);
    }
  }
}
